import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';

import { axiosInstance, apiUrl } from '../api/axiosInstance';
import { buildLogtoLoginUrl } from './logto-urls';

export type AdminPrincipal = {
  authType: 'logto-session' | 'logto-bearer' | 'admin-token';
  subject: string;
  email?: string;
};

type AdminMeResponse = {
  authenticated: true;
  admin: AdminPrincipal;
};

type LogtoSessionContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: AdminPrincipal;
  error?: Error;
  refresh: () => Promise<void>;
  login: (returnTo?: string) => void;
  logout: (options?: { returnTo?: string; redirectToLogin?: boolean }) => Promise<void>;
};

const LogtoSessionContext = createContext<LogtoSessionContextValue | undefined>(undefined);

function currentReturnTo() {
  return `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function isUnauthorized(error: unknown) {
  return error instanceof AxiosError && error.response?.status === 401;
}

export function LogtoSessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminPrincipal>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get<AdminMeResponse>('/auth/admin/me');
      setUser(response.data.admin);
      setError(undefined);
    } catch (err) {
      setUser(undefined);
      if (isUnauthorized(err)) {
        setError(undefined);
      } else {
        setError(err instanceof Error ? err : new Error('Unable to load admin session'));
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback((returnTo = currentReturnTo()) => {
    window.location.assign(buildLogtoLoginUrl(returnTo, apiUrl));
  }, []);

  const logout = useCallback(
    async (options: { returnTo?: string; redirectToLogin?: boolean } = {}) => {
      try {
        await axiosInstance.post('/auth/admin/logout');
      } finally {
        setUser(undefined);
        if (options.redirectToLogin) {
          login(options.returnTo ?? currentReturnTo());
        }
      }
    },
    [login],
  );

  const value = useMemo(
    () => ({
      isAuthenticated: !!user,
      isLoading,
      user,
      error,
      refresh,
      login,
      logout,
    }),
    [error, isLoading, login, logout, refresh, user],
  );

  return <LogtoSessionContext.Provider value={value}>{children}</LogtoSessionContext.Provider>;
}

export function useLogtoSession() {
  const context = useContext(LogtoSessionContext);
  if (!context) {
    throw new Error('useLogtoSession must be used within LogtoSessionProvider');
  }
  return context;
}
